import { useState, useEffect, useCallback } from 'react'
import {
  Plus, Shield, Edit2, Trash2, X, Check, Loader2, Eye,
} from 'lucide-react'
import { PageHeader } from '@/components/shared/PageHeader'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useAppStore } from '@/store/appStore'
import {
  getRolesCatalog,
  createRole,
  updateRole,
  deleteRole,
  listRoleEntitlements,
  createEntitlement,
  deleteEntitlement,
} from '@/features/ksec/api'
import type {
  KsecRoleCatalog,
  KsecEntitlement,
  InvolvementRole,
  ActionType,
} from '@/features/ksec/types'
import { useAssetCatalog } from '@/features/assets/useAssetCatalog'
import { RoleAccessCard } from '@/features/roles/RoleAccessCard'
import { cn } from '@/lib/utils'

// ─── Constantes ───────────────────────────────────────────────────────────────

const INVOLVEMENT_ROLES: InvolvementRole[] = [
  'ADMIN', 'USER', 'READER',
  'ACCOUNT_MANAGER', 'ACCOUNT_CONSULTANT', 'COUNTRY_MANAGER',
  'SALES_MANAGER', 'CUSTOMER_ADMINISTRATOR', 'SYSTEM_ADMIN', 'OTHER',
]

const INVOLVEMENT_LABEL: Record<string, string> = {
  ADMIN:                  'Administrador',
  USER:                   'Usuario',
  READER:                 'Lector',
  ACCOUNT_MANAGER:        'Gerente de Cuenta',
  ACCOUNT_CONSULTANT:     'Consultor de Cuenta',
  COUNTRY_MANAGER:        'Gerente de País',
  SALES_MANAGER:          'Gerente de Ventas',
  CUSTOMER_ADMINISTRATOR: 'Administrador Cliente',
  SYSTEM_ADMIN:           'Admin del Sistema',
  KASHIO_SUPER_ADMIN:     'Super Admin Kashio',
  KASHIO_ADMIN:           'Admin Kashio',
  OTHER:                  'Otro',
}

const ENTITLEMENT_ACTIONS = ['R', 'RW', 'ALL'] as const

// ─── Helpers ──────────────────────────────────────────────────────────────────

function normalizeRoles(raw: unknown): KsecRoleCatalog[] {
  if (Array.isArray(raw)) return raw as KsecRoleCatalog[]
  const obj = raw as Record<string, unknown>
  if (obj && Array.isArray(obj.items)) return obj.items as KsecRoleCatalog[]
  return []
}

// ─── AccessModal — ver y gestionar accesos de un rol ─────────────────────────

interface AccessModalProps {
  role: KsecRoleCatalog
  catalog: ReturnType<typeof useAssetCatalog>
  onClose: () => void
}

function AccessModal({ role, catalog, onClose }: AccessModalProps) {
  const [entitlements, setEntitlements] = useState<KsecEntitlement[]>([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [newItemId, setNewItemId] = useState('')
  const [newAction, setNewAction] = useState<ActionType>('RW')
  const [saving, setSaving] = useState(false)
  const [removingId, setRemovingId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await listRoleEntitlements(role.id)
      setEntitlements(Array.isArray(res.data) ? res.data : [])
    } catch {
      setEntitlements([])
    } finally {
      setLoading(false)
    }
  }, [role.id])

  useEffect(() => { void load() }, [load])

  async function handleAdd() {
    if (!newItemId) return
    setSaving(true)
    try {
      await createEntitlement(role.id, {
        digitalAssetItemPublicId: newItemId,
        action: newAction,
        function: 'Acceso configurado manualmente',
      })
      setShowAdd(false)
      setNewItemId('')
      setNewAction('RW')
      await load()
    } catch {
      await load()
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(ent: KsecEntitlement) {
    setRemovingId(ent.id)
    try {
      await deleteEntitlement(role.id, ent.id)
      await load()
    } catch {
      // ignorar
    } finally {
      setRemovingId(null)
    }
  }

  // Agrupar items del catálogo por asset para el selector
  const assetGroups = catalog.assets.map(asset => ({
    assetId:   asset.id,
    assetName: asset.name,
    items:     Array.from(catalog.byTag.values()).filter(e => e.assetId === asset.id),
  })).filter(g => g.items.length > 0)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex w-full max-w-lg flex-col rounded-2xl bg-bg-primary shadow-2xl"
           style={{ maxHeight: '85vh' }}>

        {/* Header */}
        <div className="flex items-start justify-between border-b border-border-primary px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-utility-brand-50">
              <Shield size={18} className="text-brand-600" />
            </div>
            <div>
              <h2 className="font-display font-bold text-text-primary">{role.name}</h2>
              <p className="text-xs text-text-tertiary">
                {INVOLVEMENT_LABEL[role.involvementRole] ?? role.involvementRole}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-text-tertiary hover:bg-bg-secondary transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* Accesos actuales */}
          <div>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-display font-bold text-text-primary">Accesos configurados</h3>
              <span className="rounded-full bg-bg-secondary px-2 py-0.5 text-xs text-text-tertiary">
                {entitlements.length} acceso{entitlements.length !== 1 ? 's' : ''}
              </span>
            </div>

            <RoleAccessCard
              entitlements={entitlements}
              catalog={catalog}
              loading={loading}
              compact
              emptyMessage="Este rol no tiene accesos. Agrega uno con el botón de abajo."
              onDelete={handleDelete}
              removingId={removingId ?? undefined}
            />
          </div>

          {/* Formulario agregar acceso */}
          {showAdd && (
            <div className="rounded-xl border border-brand-200 bg-brand-50/40 p-4 space-y-3">
              <p className="text-xs font-semibold text-text-secondary">Nuevo acceso</p>

              {catalog.loading ? (
                <div className="flex items-center gap-2 text-xs text-text-tertiary">
                  <Loader2 size={13} className="animate-spin" />
                  Cargando catálogo…
                </div>
              ) : (
                <select
                  value={newItemId}
                  onChange={e => setNewItemId(e.target.value)}
                  className="w-full rounded-lg border border-border-primary bg-bg-primary px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
                >
                  <option value="">— Selecciona un recurso —</option>
                  {assetGroups.map(({ assetId, assetName, items }) => (
                    <optgroup key={assetId} label={assetName}>
                      {items.map(item => (
                        <option key={item.itemPublicId} value={item.itemPublicId}>
                          {item.itemName}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              )}

              <div>
                <p className="mb-1.5 text-xs text-text-tertiary">
                  Nivel de acceso — selecciona uno:
                </p>
                <div className="grid grid-cols-3 gap-1.5">
                  {ENTITLEMENT_ACTIONS.map(a => (
                    <button
                      key={a}
                      type="button"
                      onClick={() => setNewAction(a)}
                      className={cn(
                        'flex flex-col items-center gap-0.5 rounded-lg border py-2 px-1 text-center transition-all',
                        newAction === a
                          ? 'border-brand-500 bg-brand-50 ring-2 ring-brand-300 ring-offset-1'
                          : 'border-border-primary bg-bg-primary hover:border-brand-300 hover:bg-brand-50/50',
                      )}
                    >
                      <span className={cn(
                        'text-xs font-bold',
                        newAction === a ? 'text-brand-700' : 'text-text-secondary',
                      )}>
                        {a}
                      </span>
                      <span className={cn(
                        'text-[10px] leading-tight',
                        newAction === a ? 'text-brand-500' : 'text-text-tertiary',
                      )}>
                        {a === 'R' && 'Lectura'}
                        {a === 'RW' && 'Leer/Editar'}
                        {a === 'ALL' && 'Completo'}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <Button variant="secondary" size="sm" onClick={() => setShowAdd(false)} disabled={saving}>
                  Cancelar
                </Button>
                <Button size="sm" disabled={!newItemId || saving} onClick={handleAdd}>
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                  Agregar
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-border-primary px-6 py-4">
          {!showAdd && (
            <Button variant="secondary" size="sm" onClick={() => setShowAdd(true)}>
              <Plus size={13} />
              Agregar acceso
            </Button>
          )}
          {showAdd && <div />}
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─── CreateRoleModal ──────────────────────────────────────────────────────────

interface CreateRoleModalProps {
  open: boolean
  businessTenantId: string
  onClose: () => void
  onCreated: (role: KsecRoleCatalog) => void
}

function CreateRoleModal({ open, businessTenantId, onClose, onCreated }: CreateRoleModalProps) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [involvementRole, setInvolvementRole] = useState<InvolvementRole>('OTHER')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setName(''); setDescription(''); setInvolvementRole('OTHER'); setError(null)
    }
  }, [open])

  if (!open) return null

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (name.trim().length < 2) { setError('El nombre debe tener al menos 2 caracteres.'); return }
    setSaving(true); setError(null)
    try {
      const res = await createRole({
        name: name.trim(),
        description: description.trim() || undefined,
        involvementRole,
        businessTenantPublicId: businessTenantId,
      })
      onCreated(res.data)
    } catch {
      setError('Error al crear el rol. Verifica que el tipo de rol no exista ya en la organización.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-bg-primary shadow-2xl">
        <div className="flex items-center justify-between border-b border-border-secondary px-6 py-5">
          <h2 className="font-display font-bold text-text-primary">Nuevo rol</h2>
          <button onClick={onClose} className="rounded-lg p-1.5 text-text-tertiary hover:bg-bg-secondary">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-6">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-text-secondary">Nombre *</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Ej. Supervisor de Pagos"
              className="w-full rounded-lg border border-border-primary px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-text-secondary">Descripción</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="¿Qué puede hacer este rol?"
              rows={3}
              className="w-full resize-none rounded-lg border border-border-primary px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-text-secondary">Tipo de rol *</label>
            <select
              value={involvementRole}
              onChange={e => setInvolvementRole(e.target.value as InvolvementRole)}
              className="w-full rounded-lg border border-border-primary px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
            >
              {INVOLVEMENT_ROLES.map(r => (
                <option key={r} value={r}>{INVOLVEMENT_LABEL[r] ?? r}</option>
              ))}
            </select>
          </div>

          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600">{error}</p>}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={saving}>
              Cancelar
            </Button>
            <Button type="submit" size="sm" disabled={saving}>
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Crear rol
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── EditRoleModal ────────────────────────────────────────────────────────────

interface EditRoleModalProps {
  role: KsecRoleCatalog | null
  onClose: () => void
  onUpdated: (role: KsecRoleCatalog) => void
}

function EditRoleModal({ role, onClose, onUpdated }: EditRoleModalProps) {
  const [name, setName] = useState(role?.name ?? '')
  const [description, setDescription] = useState(role?.description ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setName(role?.name ?? ''); setDescription(role?.description ?? ''); setError(null)
  }, [role])

  if (!role) return null

  const currentRole = role

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (name.trim().length < 2) { setError('El nombre debe tener al menos 2 caracteres.'); return }
    setSaving(true); setError(null)
    try {
      const res = await updateRole(currentRole.id, { name: name.trim(), description: description.trim() || undefined })
      onUpdated(res.data)
    } catch {
      setError('Error al actualizar el rol.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-bg-primary shadow-2xl">
        <div className="flex items-center justify-between border-b border-border-secondary px-6 py-5">
          <h2 className="font-display font-bold text-text-primary">Editar rol</h2>
          <button onClick={onClose} className="rounded-lg p-1.5 text-text-tertiary hover:bg-bg-secondary">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-6">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-text-secondary">Nombre *</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full rounded-lg border border-border-primary px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-text-secondary">Descripción</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
              className="w-full resize-none rounded-lg border border-border-primary px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-text-secondary">Tipo de rol</label>
            <div className="rounded-lg border border-border-primary bg-bg-secondary px-3 py-2 text-sm text-text-tertiary">
              {INVOLVEMENT_LABEL[currentRole.involvementRole] ?? currentRole.involvementRole}
            </div>
          </div>

          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600">{error}</p>}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={saving}>
              Cancelar
            </Button>
            <Button type="submit" size="sm" disabled={saving}>
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              Guardar
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── RoleCard — compacta, sin expansión ───────────────────────────────────────

interface RoleCardProps {
  role: KsecRoleCatalog
  onViewAccess: (role: KsecRoleCatalog) => void
  onEdit: (role: KsecRoleCatalog) => void
  onDelete: (role: KsecRoleCatalog) => void
}

function RoleCard({ role, onViewAccess, onEdit, onDelete }: RoleCardProps) {
  return (
    <Card className="transition-shadow hover:shadow-md">
      <CardContent className="p-5 flex flex-col gap-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-utility-brand-50">
              <Shield size={16} className="text-brand-600" />
            </div>
            <div className="min-w-0">
              <p className="truncate font-display font-bold text-text-primary">{role.name}</p>
              {role.description && (
                <p className="mt-0.5 line-clamp-2 text-xs text-text-tertiary leading-relaxed">
                  {role.description}
                </p>
              )}
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-1">
            <Button variant="ghost" size="icon" onClick={() => onEdit(role)} aria-label="Editar">
              <Edit2 size={13} />
            </Button>
            <Button variant="danger" size="icon" onClick={() => onDelete(role)} aria-label="Eliminar">
              <Trash2 size={13} />
            </Button>
          </div>
        </div>

        {/* Tipo */}
        <Badge variant="outline" className="w-fit text-xs">
          {INVOLVEMENT_LABEL[role.involvementRole] ?? role.involvementRole}
        </Badge>

        {/* CTA ver accesos */}
        <button
          type="button"
          onClick={() => onViewAccess(role)}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-border-primary bg-bg-secondary px-3 py-2 text-xs font-medium text-text-secondary transition-colors hover:border-brand-400 hover:bg-brand-50 hover:text-brand-700"
        >
          <Eye size={13} />
          Ver accesos
        </button>
      </CardContent>
    </Card>
  )
}

// ─── RolesPage ────────────────────────────────────────────────────────────────

export function RolesPage() {
  const { identity, org } = useAppStore()
  const catalog = useAssetCatalog()

  const [roles, setRoles] = useState<KsecRoleCatalog[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [showCreate, setShowCreate] = useState(false)
  const [editingRole, setEditingRole] = useState<KsecRoleCatalog | null>(null)
  const [deletingRole, setDeletingRole] = useState<KsecRoleCatalog | null>(null)
  const [accessRole, setAccessRole] = useState<KsecRoleCatalog | null>(null)
  const [deleting, setDeleting] = useState(false)

  const businessTenantId = org.current?.businessTenantId ?? ''
  const partyId = identity.partyId

  const loadRoles = useCallback(async () => {
    if (!partyId) { setLoading(false); return }
    setLoading(true); setError(null)
    try {
      const res = await getRolesCatalog(partyId, businessTenantId || undefined)
      setRoles(normalizeRoles(res.data))
    } catch {
      setError('No se pudieron cargar los roles. Intenta de nuevo.')
    } finally {
      setLoading(false)
    }
  }, [partyId, businessTenantId])

  useEffect(() => { void loadRoles() }, [loadRoles])

  function handleCreated(role: KsecRoleCatalog) {
    setRoles(prev => [role, ...prev])
    setShowCreate(false)
  }

  function handleUpdated(updated: KsecRoleCatalog) {
    setRoles(prev => prev.map(r => r.id === updated.id ? updated : r))
    setEditingRole(null)
  }

  async function handleDelete() {
    if (!deletingRole) return
    setDeleting(true)
    try {
      await deleteRole(deletingRole.id)
      setRoles(prev => prev.filter(r => r.id !== deletingRole.id))
      setDeletingRole(null)
    } catch {
      // el backend puede rechazar si hay usuarios con este rol
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Roles"
        description="Administra los roles y sus accesos dentro de la organización."
        action={
          <Button onClick={() => setShowCreate(true)}>
            <Plus size={14} /> Nuevo rol
          </Button>
        }
      />

      {loading && (
        <div className="flex items-center justify-center py-16 text-text-tertiary">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          Cargando roles…
        </div>
      )}

      {!loading && error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-600">
          {error}
          <button className="ml-2 underline" onClick={() => void loadRoles()}>Reintentar</button>
        </div>
      )}

      {!loading && !error && roles.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border-primary py-16 text-center">
          <Shield size={32} className="mb-3 text-text-tertiary" />
          <p className="font-medium text-text-secondary">No hay roles configurados</p>
          <p className="mt-1 text-sm text-text-tertiary">
            Crea el primer rol para comenzar a asignar accesos.
          </p>
          <Button className="mt-4" onClick={() => setShowCreate(true)}>
            <Plus size={14} /> Nuevo rol
          </Button>
        </div>
      )}

      {!loading && !error && roles.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {roles.map(role => (
            <RoleCard
              key={role.id}
              role={role}
              onViewAccess={setAccessRole}
              onEdit={setEditingRole}
              onDelete={setDeletingRole}
            />
          ))}
        </div>
      )}

      {/* Modal: Ver / gestionar accesos */}
      {accessRole && (
        <AccessModal
          role={accessRole}
          catalog={catalog}
          onClose={() => setAccessRole(null)}
        />
      )}

      {/* Modal: Crear rol */}
      <CreateRoleModal
        open={showCreate}
        businessTenantId={businessTenantId}
        onClose={() => setShowCreate(false)}
        onCreated={handleCreated}
      />

      {/* Modal: Editar rol */}
      <EditRoleModal
        role={editingRole}
        onClose={() => setEditingRole(null)}
        onUpdated={handleUpdated}
      />

      {/* Modal: Confirmar eliminación */}
      {deletingRole && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-bg-primary p-6 shadow-2xl">
            <h2 className="font-display font-bold text-text-primary">Eliminar rol</h2>
            <p className="mt-2 text-sm text-text-secondary">
              ¿Estás seguro de que quieres eliminar{' '}
              <span className="font-medium">{deletingRole.name}</span>?
              Esta acción no se puede deshacer.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={() => setDeletingRole(null)} disabled={deleting}>
                Cancelar
              </Button>
              <Button variant="danger" size="sm" onClick={handleDelete} disabled={deleting}>
                {deleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                Eliminar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
